import { Draw, DrawSchedule } from '@/models/bank-models.model';
import { DrawsService } from '@/services/api/draws.service';
import { CommonModule } from '@angular/common';
import { Component, effect, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { FluidModule } from 'primeng/fluid';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';
import { ToggleSwitchModule } from 'primeng/toggleswitch';

@Component({
    selector: 'app-draw-editor',
    imports: [
        TableModule,
        ButtonModule,
        FormsModule,
        CommonModule,
        InputTextModule,
        InputNumberModule,
        FluidModule,
        ToggleSwitchModule
    ],
    template: `

    <p-fluid>
        <div class="flex flex-col md:flex-row gap-8">
                <div class="flex flex-col gap-4 w-full">
                    <div class="flex flex-wrap gap-4">
                        <div class="flex flex-col grow basis-0 gap-2">
                            <label for="name2">Title</label>
                            <input pInputText id="name2" class="w-full" type="text" [(ngModel)]="currentSchedule.Title" />
                        </div>
                        <div class="flex flex-col grow basis-0 gap-2">
                            <label for="email2">Cash Schedule</label>
                            <p-toggleswitch name="DraftOnly" [(ngModel)]="currentSchedule.isCash" />
                        </div>
                       
                    </div>
                </div>
            </div>
    <p-table 
    [value]="currentSchedule.Draws" 
    dataKey="DrawID" 
    editMode="row" 
    [tableStyle]="{'min-width': '50rem'}"
    >
    <ng-template #caption>
      <div class="flex">
        <span class="text-lg font-bold">Draws</span>
            <p-button label="Add Draw" class="ml-auto" [outlined]="true" icon="pi pi-plus" (click)="newDraw()" />
        </div>
    </ng-template>
    <ng-template pTemplate="header">
        <tr>
            <th style="width:80%">Event</th>
            <th style="width:10%">Percentage</th>
            <th style="width:10%"></th>
        </tr>
    </ng-template>
    <ng-template pTemplate="body" let-draw let-editing="editing" let-ri="rowIndex">
        <tr [pEditableRow]="draw">
            <td style="width:80%">
                <p-cellEditor>
                    <ng-template pTemplate="input">
                        <input
                            pInputText 
                            type="text" 
                            styleClass="w-full"
                            class="w-full"
                            [(ngModel)]="draw.Title" />
                    </ng-template>
                    <ng-template pTemplate="output">
                        {{draw.Title}}
                    </ng-template>
                </p-cellEditor>
            </td>
            <td style="width:10%">
                <p-cellEditor>
                    <ng-template pTemplate="input">
                        <input 
                            pInputText type="number" 
                            class="w-full"
                            [(ngModel)]="draw.Percentage" 
                            required />
                    </ng-template>
                    <ng-template pTemplate="output">
                        {{draw.Percentage}}
                    </ng-template>
                </p-cellEditor>
            </td>
            <td style="width:10%">
                <div class="flex align-items-center justify-content-center gap-2">
                    <button 
                        pButton 
                        pRipple 
                        *ngIf="!editing"
                        type="button" 
                        icon="pi pi-trash" 
                        severity="danger"
                        (click)="confirmDeleteDraw($event,draw)" 
                        class="p-button-rounded p-button-text">
                    </button>
                    <button
                        *ngIf="!editing"
                        pButton
                        pRipple
                        type="button"
                        pInitEditableRow
                        icon="pi pi-pencil"
                        (click)="onRowEditInit(draw)"
                        text
                        rounded
                        severity="secondary"
                    ></button>
                    <button
                        *ngIf="editing"
                        pButton
                        pRipple
                        type="button"
                        pSaveEditableRow
                        icon="pi pi-check"
                        (click)="onRowEditSave(draw)"
                        text
                        rounded
                        severity="secondary"
                    ></button>
                    <button
                        *ngIf="editing"
                        pButton
                        pRipple
                        type="button"
                        pCancelEditableRow
                        icon="pi pi-times"
                        (click)="onRowEditCancel(draw, ri)"
                        text
                        rounded
                        severity="secondary"
                    ></button>
                </div>
            </td>
        </tr>
    </ng-template>
</p-table>
            <div class="flex flex-col md:flex-row gap-4">
                    <div class="flex flex-wrap gap-2 w-full">
                    <button pButton type="button" label="Cancel" icon="pi pi-times" severity="secondary">
                        </button>
                    </div>
                    <div class="flex flex-wrap gap-2 w-full">
                        <button pButton type="submit" label="Save" icon="pi pi-save" (click)="onSave()" [disabled]="!enableSaving()">
                    </button>
                    </div>
                </div>
    </p-fluid>

    
    `
})

export class DrawEditorComponent {
  currentSchedule: DrawSchedule = new DrawSchedule();
  public drawDataService = inject(DrawsService);
  private confirmationService = inject(ConfirmationService);
  private messageService = inject(MessageService);

  clonedProducts: { [drawId: string]: Draw } = {};

  constructor(
  ) {
    effect(() => {
    const schedule = this.drawDataService.selectedDraw();
    if(schedule) {
      console.log('schedule', schedule);
      this.currentSchedule = schedule;
    } else {
      console.log('no schedule');
      this.currentSchedule = new DrawSchedule();
    }
  });
  }

  onSave() {
    console.log('save');
  }

  onClose() {
    this.drawDataService.selectedDraw.set(null);
  }

  newDraw() {
    const total = this.currentSchedule.Draws.reduce((acc, draw) => acc + draw.Percentage, 0);
    const percentage = 1 - total;
    const draw = new Draw(percentage, 'New Draw');
    if(this.currentSchedule.Draws) {
      this.currentSchedule.Draws.push(draw);
    } else {
      this.currentSchedule.Draws = [draw];
    }
  }

  enableSaving() {
    return this.currentSchedule.Title && this.currentSchedule.Draws.length > 0 && this.isWhole();
  }

  confirmDeleteDraw(event: Event,draw: Draw) {
    this.confirmationService.confirm({
      target: event.target as EventTarget,
      message: 'Are you sure you want to delete this draw?',
      header: 'Confirmation',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.deleteDraw(draw);
      }
    });
  }

  deleteDraw(draw: Draw) {
    if(this.currentSchedule.Draws) {
      this.currentSchedule.Draws = this.currentSchedule.Draws.filter(d => d !== draw);
    }
  }

  isWhole() {
    if(this.currentSchedule.Draws) {
      return this.currentSchedule.Draws.reduce((acc, draw) => acc + draw.Percentage, 0) === 100;
    }
    return false;
  }

  isPartial() {
    if(this.currentSchedule.Draws) {
      return this.currentSchedule.Draws.reduce((acc, draw) => acc + draw.Percentage, 0) < 100;
    }
    return false;

}

onRowEditInit(draw: Draw) {
  this.clonedProducts[draw.DrawID.toString()] = { ...draw };
}

onRowEditSave(draw: Draw) {
  if (draw.Percentage > 0) {
      delete this.clonedProducts[draw.DrawID.toString()];
  } else {
      this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Invalid Price' });
  }
}

onRowEditCancel(draw: Draw, index: number) {
  this.currentSchedule.Draws[index] = this.clonedProducts[draw.DrawID.toString()];
  delete this.clonedProducts[draw.DrawID.toString()];
}
}
